import { useState, useEffect, useCallback } from "react";
import api from "@/services/api";

interface UseApiOptions {
  enabled?: boolean;
}

export function useApi<T>(url: string, options: UseApiOptions = {}) {
  const { enabled = true } = options;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(url);
      setData(res.data.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    if (enabled) fetchData();
  }, [enabled, fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useMutation<T, D = unknown>(method: "post" | "patch" | "put" | "delete", url: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = async (payload?: D): Promise<T | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api[method](url, payload);
      return res.data.data;
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || "Request failed";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  };

  return { mutate, loading, error };
}

